if (!customElements.get('brand-carousel')) {
  customElements.define(
    'brand-carousel',
    class BrandCarousel extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-carousel-viewport]');
        this.items = Array.from(this.querySelectorAll('[data-carousel-item]'));
        this.dotsContainer = this.querySelector('[data-carousel-dots]');
        this.status = this.querySelector('[data-carousel-status]');
        this.previousButton = this.querySelector('.brand-carousel__arrow--prev');
        this.nextButton = this.querySelector('.brand-carousel__arrow--next');
        this.autoplay = this.dataset.autoplay === 'true';
        this.interval = Number(this.dataset.interval) || 5000;
        this.desktopVisible = this.getPositiveInteger(this.dataset.columnsDesktop, 7);
        this.tabletVisible = this.getPositiveInteger(this.dataset.columnsTablet, 3);
        this.mobileVisible = this.getPositiveInteger(this.dataset.columnsMobile, 2);
        this.canPauseOnInteraction = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        this.currentPage = 0;

        if (!this.viewport || this.items.length < 2) return;

        this.handlePrevious = () => this.goToPage(this.currentPage - 1, true);
        this.handleNext = () => this.goToPage(this.currentPage + 1, true);
        this.handleScroll = this.debounce(() => this.updateFromScroll(), 80);
        this.handleResize = this.debounce(() => this.refresh(), 150);
        this.pause = () => this.stopAutoplay();
        this.resume = () => this.startAutoplay();
        this.handleVisibility = () => document.hidden ? this.stopAutoplay() : this.startAutoplay();

        this.previousButton?.addEventListener('click', this.handlePrevious);
        this.nextButton?.addEventListener('click', this.handleNext);
        this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
        if (this.canPauseOnInteraction) {
          this.addEventListener('mouseenter', this.pause);
          this.addEventListener('mouseleave', this.resume);
          this.addEventListener('focusin', this.pause);
          this.addEventListener('focusout', this.resume);
        }
        window.addEventListener('resize', this.handleResize);
        document.addEventListener('visibilitychange', this.handleVisibility);

        this.refresh();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        this.previousButton?.removeEventListener('click', this.handlePrevious);
        this.nextButton?.removeEventListener('click', this.handleNext);
        this.viewport?.removeEventListener('scroll', this.handleScroll);
        if (this.canPauseOnInteraction) {
          this.removeEventListener('mouseenter', this.pause);
          this.removeEventListener('mouseleave', this.resume);
          this.removeEventListener('focusin', this.pause);
          this.removeEventListener('focusout', this.resume);
        }
        window.removeEventListener('resize', this.handleResize);
        document.removeEventListener('visibilitychange', this.handleVisibility);
      }

      getVisibleCount() {
        if (window.matchMedia('(min-width: 990px)').matches) return this.desktopVisible;
        if (window.matchMedia('(min-width: 750px)').matches) return this.tabletVisible;
        return this.mobileVisible;
      }

      getPositiveInteger(value, fallback) {
        const parsedValue = Number.parseInt(value, 10);
        return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
      }

      refresh() {
        this.visibleCount = this.getVisibleCount();
        this.pageCount = Math.ceil(this.items.length / this.visibleCount);
        this.currentPage = Math.min(this.currentPage, this.pageCount - 1);
        this.renderDots();
        this.updateControls();
        this.startAutoplay();
      }

      renderDots() {
        if (!this.dotsContainer) return;
        this.dotsContainer.replaceChildren();

        for (let index = 0; index < this.pageCount; index += 1) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'brand-carousel__dot';
          button.setAttribute('aria-label', `Ir al grupo ${index + 1} de ${this.pageCount}`);
          button.addEventListener('click', () => this.goToPage(index, true));
          this.dotsContainer.appendChild(button);
        }

        this.dotsContainer.hidden = this.pageCount < 2;
      }

      goToPage(page, manual = false) {
        if (this.pageCount < 2) return;
        const normalizedPage = (page + this.pageCount) % this.pageCount;
        this.currentPage = normalizedPage;
        const maximumScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
        const firstItemIndex = normalizedPage * this.visibleCount;
        const targetItem = this.items[Math.min(firstItemIndex, this.items.length - 1)];
        const firstItemOffset = this.items[0]?.offsetLeft || 0;
        const targetScroll = Math.min(Math.max((targetItem?.offsetLeft || 0) - firstItemOffset, 0), maximumScroll);
        this.viewport.scrollTo({ left: targetScroll, behavior: 'smooth' });
        this.updateControls();
        if (manual) this.restartAutoplay();
      }

      updateFromScroll() {
        const maximumScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
        const progress = maximumScroll > 0 ? this.viewport.scrollLeft / maximumScroll : 0;
        this.currentPage = Math.min(Math.round(progress * (this.pageCount - 1)), this.pageCount - 1);
        this.updateControls();
      }

      updateControls() {
        const dots = this.dotsContainer?.querySelectorAll('.brand-carousel__dot') || [];
        dots.forEach((dot, index) => dot.setAttribute('aria-current', index === this.currentPage ? 'true' : 'false'));
        if (this.status && this.pageCount > 1) {
          this.status.textContent = `Grupo ${this.currentPage + 1} de ${this.pageCount}`;
        }
      }

      startAutoplay() {
        this.stopAutoplay();
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!this.autoplay || reducedMotion || document.hidden || this.pageCount < 2) return;
        this.timer = window.setInterval(() => this.goToPage(this.currentPage + 1), this.interval);
      }

      stopAutoplay() {
        if (this.timer) window.clearInterval(this.timer);
        this.timer = null;
      }

      restartAutoplay() {
        this.startAutoplay();
      }

      debounce(callback, wait) {
        let timeout;
        return (...args) => {
          window.clearTimeout(timeout);
          timeout = window.setTimeout(() => callback.apply(this, args), wait);
        };
      }
    }
  );
}
